import {scheduleAfterNextPaint} from '@github-ui/copilot-chat-state/schedule-after-next-paint'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useDynamicTextareaHeight} from '@github-ui/use-dynamic-textarea-height'
import {ScopedCommands} from '@github-ui/ui-commands'
import {AlertIcon, PaperAirplaneIcon} from '@primer/octicons-react'
import {IconButton, useMergedRefs} from '@primer/react'
import {useEffect, useRef, useState} from 'react'

import styles from './SteeringCommandInput.module.css'

// Maximum height for textarea in pixels, equivalent to approximately 6 lines (legacy JS autosize only).
const MAX_TEXTAREA_HEIGHT_PX = 256

type SteeringCommandInputProps = {
  onSubmit: (message: string) => void
  error?: string | null
}

export function SteeringCommandInput({onSubmit, error}: SteeringCommandInputProps) {
  const [value, setValue] = useState('')

  // Gate the CSS `field-sizing` autosize behind a flag, falling back to the legacy per-keystroke JS
  // autosize when it's off.
  const cssAutosize = isFeatureEnabled('copilot_css_textarea_autosize')

  // CSS autosize (flag on): `field-sizing: content` with a measured fallback on browsers that lack it.
  // The CSS `max-height` caps growth and the textarea scrolls past it; clearing the value collapses it
  // back to one line automatically. Disabled when the flag is off so the hook does no measurement.
  const {ref: heightRef, ...heightStyles} = useDynamicTextareaHeight({disabled: !cssAutosize, value})

  // Legacy JS autosize (flag off): measures `scrollHeight` and writes the height on every keystroke.
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const autosizeFrameRef = useRef<number | null>(null)
  const mergedRef = useMergedRefs(heightRef, textareaRef)

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
  }

  useEffect(() => {
    if (cssAutosize) return
    const textarea = textareaRef.current
    if (!textarea) return

    function adjustHeight() {
      if (!textarea) return
      // Reset first so scrollHeight reflects the content's natural height, including when text shrinks.
      textarea.style.height = 'auto'
      // Then apply the measured height, capped so the textarea scrolls after reaching the max.
      textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`
    }

    // Resizing the textarea changes its height, reads the new scrollHeight,
    // then writes the height back. If we do all of that before the current
    // frame paints, it delays the paint and hurts INP. That means the typed
    // character can feel sluggish. So we let the browser paint first so the
    // new character shows up quickly. We resize in the next frame so the
    // textarea height still matches the new text without much delay.
    return scheduleAfterNextPaint(autosizeFrameRef, adjustHeight)
  }, [value, cssAutosize])

  const handleSubmit = () => {
    if (value.trim()) {
      onSubmit(value)
      setValue('')
      // Legacy path: reset textarea height (the CSS path collapses automatically when the value clears).
      if (!cssAutosize && textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  return (
    <ScopedCommands commands={{'copilot-chat:send-message': handleSubmit}}>
      <div className={styles.steeringCommandInputContainer}>
        <div className={styles.steeringCommandInputWrapper}>
          <textarea
            ref={mergedRef}
            className={styles.steeringCommandInputTextarea}
            style={cssAutosize ? heightStyles : undefined}
            value={value}
            onChange={handleChange}
            placeholder="What would you like to do next?"
            autoComplete="off"
            autoCorrect="on"
            rows={1}
          />
          <IconButton
            className={styles.steeringCommandInputSubmitButton}
            variant="invisible"
            size="small"
            icon={PaperAirplaneIcon}
            aria-label="Send"
            disabled={!value.trim()}
            onClick={handleSubmit}
          />
        </div>
        {error && (
          <div className={styles.errorMessage}>
            <AlertIcon className={styles.errorIcon} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </ScopedCommands>
  )
}
